import { useLocation } from "react-router-dom";
import { isString } from "./string";

/**
 * Builds a redirect URL by appending or replacing a `destination` query parameter
 * with the current location's pathname and search string.
 *
 * @param to - The base URL or path to which the user should be redirected.
 * @param options - Optional parameters.
 * @param options.location - The current location object, typically obtained from `useLocation()`.
 * @returns The URL with the `destination` parameter set to the current location.
 *
 * @remarks
 * - If the `to` string already contains a `destination` parameter, it will be replaced.
 * - If the `to` string contains other query parameters but not `destination`, it will be appended.
 * - If the `to` string contains no query parameters, `destination` will be added as the first parameter.
 */
export const buildRedirectUrl = (to: string, options?: { location?: ReturnType<typeof useLocation> }): string => {
    // If 'to' is a string, handle destination logic for sign-in redirects
    let destination = to;
    if (!isString(to)) return destination;

    const { pathname, search } = options?.location || window.location;
    // Always append current location as destination param
    const encodedCurrent = encodeURIComponent(pathname + search);
    if (to.includes("?")) {
        // If 'destination' param exists, replace it; otherwise, append it
        if (to.includes("destination=")) {
            destination = to.replace(/destination=([^&]*)/, `destination=${encodedCurrent}`);
        } else {
            destination = `${to}&destination=${encodedCurrent}`;
        }
    } else {
        destination = `${to}?destination=${encodedCurrent}`;
    }
    return destination;
}